import React, { useEffect, useState } from 'react';
import { Layout, Select, Message } from 'htlib-copy';

import { useGetSingleMissionChecks } from 'hooks/checks';
import customTypes from 'types';

import AsyncUI from 'components/AsyncUI';
import Pagination from 'components/Pagination';
import { TABS } from 'utils/constants';

import { isMd5, useUrlParam } from './utils';

import ImageTile from './ImageTile';
import Filters from './Filters';
import Tabs from './Tabs';

import './Checker.scss';

const { Row, Column: Col } = Layout;

const ITEMS_PER_PAGE = 24;
const ITEMS_PER_ROW = 4;
const NO_FOR_ALL_TAB = [{ caption: 'No for all', value: 'no_for_all' }];

export default function Checker({ missionEntities }) {
  const { getUrlParam, setUrlParam } = useUrlParam();

  const tagValue = getUrlParam('tags');
  const entityId = getUrlParam('entity');
  const limit = getUrlParam('limit') || ITEMS_PER_PAGE;
  const currentPage = getUrlParam('page') || 1;
  const indexName = getUrlParam('index');
  const urlMd5 = getUrlParam('image_md5');
  const imageMd5 = isMd5(urlMd5) ? urlMd5 : null;

  const projection = [
    '_id',
    'image_md5',
    'coords',
    'index_name',
    'check_no_for_all',
    'labeling_no_for_all',
    'index_name',
    `labeling.${entityId}`,
    `check.${entityId}`,
  ];

  const [itemsPerRow, setItemsPerRow] = useState(ITEMS_PER_ROW);
  const [loadedImages, setLoadedImages] = useState(0);
  const [queryParams, setQueryParams] = useState({
    entityId,
    tagValue,
    indexName,
    imageMd5,
    limit,
    skip: limit * (currentPage - 1),
    projection: projection.join(','),
  });

  useEffect(() => {
    if (tagValue === 'no_for_all') {
      setQueryParams({
        tagValue,
        imageMd5,
        limit,
        skip: limit * (currentPage - 1),
        projection: projection.join(','),
      });
    } else {
      setQueryParams({
        entityId,
        tagValue,
        indexName,
        imageMd5,
        limit,
        skip: limit * (currentPage - 1),
        projection: projection.join(','),
      });
    }
    // eslint-disable-next-line
  }, [tagValue, entityId, currentPage, imageMd5, limit, indexName]);

  const colWide = Math.floor(12 / itemsPerRow);

  const checksQuery = useGetSingleMissionChecks(queryParams);

  const { checks = [], checkCount } = checksQuery.data || {};

  function onPageChange(pageNum) {
    setQueryParams(params => ({
      ...params,
      limit,
      skip: limit * (pageNum - 1),
    }));
    setUrlParam('page', pageNum);
  }

  return (
    <div className="Checker">
      <Filters entities={missionEntities} />

      <div className="Grid__options">
        <Select
          items={['6', '4', '3', '2', '1']}
          placeholder="Rows of"
          onSelect={rows => setItemsPerRow(rows || 4)}
          selectedValue={itemsPerRow}
        />
        <Select
          items={['96', '48', '24', '12', '6']}
          placeholder="Images per page"
          onSelect={value => {
            setUrlParam('limit', value);
          }}
          selectedValue={Number(limit)}
        />
      </div>

      <Tabs activeTab={tagValue || 'yes'} tabs={tagValue === 'no_for_all' ? NO_FOR_ALL_TAB : TABS}>
        <AsyncUI query={checksQuery}>
          {checks.length > 0 ? (
            <Row gutter="no-gut">
              {checks.map((check, idx) => (
                <Col key={`${check.id}-${check.entityId}`} wides={[{ wide: colWide }]}>
                  <ImageTile
                    check={check}
                    onLoad={() => {
                      setLoadedImages(num => num + 1);
                    }}
                    canLoad={idx <= loadedImages + 1}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Message type="info">No images to show</Message>
          )}
        </AsyncUI>
      </Tabs>

      <Pagination
        currentPage={Number(currentPage)}
        onPageChange={onPageChange}
        totalItems={checkCount}
        itemsPerPage={Number(limit)}
      />
    </div>
  );
}

Checker.propTypes = {
  missionEntities: customTypes.misc.missionEntities.isRequired,
};
